/**
 * Client-side audio player state (Zustand). Playback session (queue, index,
 * play/pause) is authoritative on the server — see usePlaybackSync.
 */
import { create } from "zustand";
import type { Track } from "@memes/shared";
import { buildActiveOwnersForQueue, type ActiveOwnersMap } from "@memes/shared";
import { playbackApi } from "../lib/api";
import { discordApi } from "../lib/api";
import {
  applyPlaybackApiSnapshot,
  clearLocalPlaybackControl,
  isApplyingRemotePlayback,
  markLocalPlaybackControl,
  shouldResumePlayback,
  syncBrowserTransport,
} from "../lib/playbackSync";
import { clientLog } from "../lib/clientLogger";
import { isWebAudioGraphAttached } from "../lib/audioGraphRuntime";

interface PlayerState {
  audio: HTMLAudioElement | null;
  queue: Track[];
  index: number;
  currentTime: number;
  duration: number | null;
  isPlaying: boolean;
  volume: number;
  muted: boolean;
  normalize: boolean;
  normalizeGainDb: number;
  voiceConnected: boolean;
  activeOwners: ActiveOwnersMap;
  timelineRevision: number;

  attachAudio: (el: HTMLAudioElement | null) => void;
  setVoiceConnected: (connected: boolean) => void;
  /** Local queue mirror (prefer PUT /queue on server, then SSE applies). */
  setQueue: (queue: Track[], startIndex?: number) => void;
  clearQueueLocal: () => void;
  appendToQueueLocal: (tracks: Track[]) => void;
  reorderQueueLocal: (fromIndex: number, toIndex: number) => void;
  setOwnerActive: (owner: string, active: boolean) => void;
  play: (index?: number) => void;
  pause: () => void;
  next: () => void;
  prev: () => void;
  seek: (time: number) => void;
  setVolume: (v: number) => void;
  setMuted: (m: boolean) => void;
  setNormalize: (n: boolean) => void;
  setNormalizeGainDb: (db: number) => void;
}

const LS_VOLUME = "jukebox.volume.v6";

let seekDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let volumeDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let seekApiTimer: ReturnType<typeof setTimeout> | null = null;

function readNumber(key: string, fallback: number): number {
  try {
    const v = localStorage.getItem(key);
    if (v == null) return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function scheduleVoiceSeek(seconds: number): void {
  if (seekDebounceTimer) clearTimeout(seekDebounceTimer);
  seekDebounceTimer = setTimeout(() => {
    seekDebounceTimer = null;
    void discordApi.voiceSeek(seconds).catch((err) =>
      clientLog.error("playback", "Discord voice seek failed", { err: String(err) }),
    );
  }, 200);
}

function scheduleVoiceVolume(volume: number, normalize?: boolean): void {
  if (volumeDebounceTimer) clearTimeout(volumeDebounceTimer);
  volumeDebounceTimer = setTimeout(() => {
    volumeDebounceTimer = null;
    void discordApi.voiceVolume(volume, normalize).catch((err) =>
      clientLog.error("playback", "Discord voice volume failed", { err: String(err) }),
    );
  }, 150);
}

export const usePlayer = create<PlayerState>((set, get) => ({
  audio: null,
  queue: [],
  index: 0,
  currentTime: 0,
  duration: null,
  isPlaying: false,
  volume: readNumber(LS_VOLUME, 1),
  muted: false,
  normalize: false,
  normalizeGainDb: -6,
  voiceConnected: false,
  activeOwners: {},
  timelineRevision: 1,

  attachAudio: (el) => set({ audio: el }),

  setVoiceConnected: (connected) => {
    const s = get();
    if (s.audio) applyVoiceOutputMode(s.audio, connected, s.volume, s.muted);
    if (connected) scheduleVoiceVolume(s.volume, s.normalize);
    set({ voiceConnected: connected });
  },

  setQueue: (queue, startIndex = 0) => {
    set({
      queue,
      index: startIndex,
      activeOwners: buildActiveOwnersForQueue(queue),
    });
  },

  appendToQueueLocal: (tracks) => {
    if (tracks.length === 0) return;
    const s = get();
    const queue = [...s.queue, ...tracks];
    set({ queue, activeOwners: buildActiveOwnersForQueue(queue) });
  },

  clearQueueLocal: () => {
    const s = get();
    s.audio?.pause();
    set({
      queue: [],
      index: 0,
      currentTime: 0,
      duration: null,
      isPlaying: false,
      activeOwners: {},
    });
  },

  reorderQueueLocal: (fromIndex, toIndex) => {
    const s = get();
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= s.queue.length || toIndex >= s.queue.length) return;
    const queue = [...s.queue];
    const [moved] = queue.splice(fromIndex, 1);
    if (!moved) return;
    queue.splice(toIndex, 0, moved);

    let newIndex = s.index;
    if (fromIndex === newIndex) {
      newIndex = toIndex;
    } else if (fromIndex < newIndex && toIndex >= newIndex) {
      newIndex -= 1;
    } else if (fromIndex > newIndex && toIndex <= newIndex) {
      newIndex += 1;
    }

    set({ queue, index: newIndex });
  },

  setOwnerActive: (owner, active) => {
    const owners = { ...get().activeOwners, [owner]: active };
    markLocalPlaybackControl();
    set({ activeOwners: owners });
    void playbackApi.setActiveOwners(owners).catch((err) => {
      clearLocalPlaybackControl();
      clientLog.error("playback", "Failed to sync active owners", { err: String(err) });
    });
  },

  play: (index) => {
    markLocalPlaybackControl();
    const s = get();
    const idx = typeof index === "number" ? index : s.index;
    const resumingSame = shouldResumePlayback(idx);

    set({ isPlaying: true });
    syncBrowserTransport("play", {
      index: idx,
      currentTime: resumingSame ? s.currentTime : 0,
    });

    const api = resumingSame ? playbackApi.resume() : playbackApi.play(idx);
    void api
      .then((res) => applyPlaybackApiSnapshot(res.snapshot, true))
      .catch((err) => {
        clearLocalPlaybackControl();
        clientLog.error("playback", "Playback play failed", { err: String(err) });
      });
  },

  pause: () => {
    markLocalPlaybackControl();
    const s = get();
    const currentTime = s.audio?.currentTime ?? s.currentTime;
    set({ isPlaying: false });
    syncBrowserTransport("pause");
    void playbackApi
      .pause(currentTime)
      .then((res) => applyPlaybackApiSnapshot(res.snapshot, true))
      .catch((err) => {
        clearLocalPlaybackControl();
        clientLog.error("playback", "Playback pause failed", { err: String(err) });
      });
  },

  next: () => {
    markLocalPlaybackControl();
    void playbackApi
      .next()
      .then((res) => applyPlaybackApiSnapshot(res.snapshot, true))
      .catch((err) => {
        clearLocalPlaybackControl();
        clientLog.error("playback", "Playback next failed", { err: String(err) });
      });
  },

  prev: () => {
    markLocalPlaybackControl();
    void playbackApi
      .prev()
      .then((res) => applyPlaybackApiSnapshot(res.snapshot, true))
      .catch((err) => {
        clearLocalPlaybackControl();
        clientLog.error("playback", "Playback prev failed", { err: String(err) });
      });
  },

  seek: (time) => {
    const t = Math.max(0, time);
    const s = get();
    if (s.audio) s.audio.currentTime = t;
    set({ currentTime: t });
    if (s.voiceConnected) {
      scheduleVoiceSeek(t);
    }
    if (isApplyingRemotePlayback()) return;
    if (seekApiTimer) clearTimeout(seekApiTimer);
    seekApiTimer = setTimeout(() => {
      seekApiTimer = null;
      void playbackApi
        .seek(t)
        .then((res) => applyPlaybackApiSnapshot(res.snapshot))
        .catch((err) => clientLog.error("playback", "Playback seek failed", { err: String(err) }));
    }, 250);
  },

  setVolume: (v) => {
    const s = get();
    if (s.audio) applyVoiceOutputMode(s.audio, s.voiceConnected, v, s.muted);
    try {
      localStorage.setItem(LS_VOLUME, String(v));
    } catch {
      /* ignore */
    }
    set({ volume: v });
    if (s.voiceConnected) scheduleVoiceVolume(v, s.normalize);
  },

  setMuted: (muted) => {
    const s = get();
    if (s.audio) applyVoiceOutputMode(s.audio, s.voiceConnected, s.volume, muted);
    set({ muted });
  },

  setNormalize: (n) => {
    set({ normalize: n });
    const cur = get();
    void playbackApi.settings({ enabled: n, preampDb: cur.normalizeGainDb }).catch((err) =>
      clientLog.error("playback", "Failed to sync normalization", { err: String(err) }),
    );
    if (cur.voiceConnected) scheduleVoiceVolume(cur.volume, n);
  },

  setNormalizeGainDb: (db) => {
    set({ normalizeGainDb: db });
    const cur = get();
    void playbackApi
      .settings({ enabled: cur.normalize, preampDb: db })
      .catch((err) =>
        clientLog.error("playback", "Failed to sync normalize gain", { err: String(err) }),
      );
  },
}));

/** Convert a track into a streamable URL served by the backend. */
export function mediaUrl(track: Track): string {
  return `/media/${encodeURIComponent(track.filename)}`;
}

/** Discord is the audible output when connected. Web Audio owns volume only while the gain graph is active. */
export function applyVoiceOutputMode(
  el: HTMLAudioElement,
  voiceConnected: boolean,
  volume: number,
  userMuted: boolean,
  webAudioOwnsVolume = false,
): void {
  if (voiceConnected) {
    el.muted = true;
    el.volume = 0;
  } else {
    el.muted = userMuted;
    el.volume = webAudioOwnsVolume || isWebAudioGraphAttached() ? 1 : Math.max(0, Math.min(1, volume));
  }
}

export { isOwnerActive } from "@memes/shared";
